import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import type { AxiosResponse } from 'axios';
import type ApiLoyaltyCard from 'src/core/models/api/ApiLoyaltyCard';
import type ApiLoyaltyResponse from 'src/core/models/api/responses/ApiLoyaltyResponse';
import { reportOrRethrow } from 'src/core/utils/ApiCallbacks';
import toErrorMessage from 'src/core/utils/ErrorMessages';
import Translations from 'src/core/utils/Translations';
import Constants from 'src/core/utils/Constants';
import ThermenRepository from 'src/data/repositories/ThermenRepository';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

// A DMP from before these fields clears the properties instead of measuring 'undefined'.
const loyaltyUserProperties = (status: ApiLoyaltyResponse['status'] | undefined) => ({
	[Constants.analyticsUserPropertyLoyaltyStatus]:
		typeof status?.visit_status === 'number' ? String(status.visit_status) : null,
	[Constants.analyticsUserPropertyBathcard]:
		typeof status?.has_bath_card === 'boolean' ? String(status.has_bath_card) : null,
});

const initialState = {
	loyalty: null as ApiLoyaltyResponse | null,
	isLoading: false,
	errorMessage: null as string | null,
	selectedBenefit: null as ApiLoyaltyCard | null,
	resortPickerCard: null as ApiLoyaltyCard | null,
	entranceKey: 0,
};

export const useLoyaltyStore = create(
	combine({ ...initialState }, (set, get) => {
		// Overlapping refetches and a logout both leave older responses in flight, so only the latest may land.
		let latestRequestId = 0;

		const init = () =>
			set({
				isLoading: false,
				errorMessage: null,
				selectedBenefit: null,
				resortPickerCard: null,
			});

		// An open sheet holds a snapshot, so it would keep showing the state the card had before the refetch
		const refreshedCard = (data: ApiLoyaltyResponse, open: ApiLoyaltyCard | null) => {
			if (open === null) {
				return null;
			}

			return (data.benefits?.cards ?? []).find((card) => card.id === open.id) ?? null;
		};

		const getApiLoyalty = async ({
			onSuccess,
			onError,
			onFinally,
		}: {
			onSuccess?: (response: AxiosResponse<ApiLoyaltyResponse>) => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		} = {}) => {
			const requestId = ++latestRequestId;
			const isStale = () => requestId !== latestRequestId;

			if (get().loyalty === null) {
				set({ isLoading: true });
			}
			set({ errorMessage: null });

			await ThermenRepository.getLoyalty()
				.then(async (response) => {
					if (isStale()) {
						return;
					}

					const data: ApiLoyaltyResponse | null = response.data;

					if (!data) {
						set({ errorMessage: Translations.unknownErrorOccurred });
						return;
					}

					set({
						loyalty: data,
						selectedBenefit: refreshedCard(data, get().selectedBenefit),
						resortPickerCard: refreshedCard(data, get().resortPickerCard),
					});
					useServicesStore.getState().setAnalyticsUserProperties(loyaltyUserProperties(data.status));
					await onSuccess?.(response);
				})
				.catch((error: ApiRequestError) => {
					if (!isStale()) {
						set({ errorMessage: toErrorMessage(error) });
					}
					reportOrRethrow(error, onError);
				})
				.finally(() => {
					if (!isStale()) {
						set({ isLoading: false });
					}
					onFinally?.();
				});
		};

		const reset = () => {
			latestRequestId += 1;
			set({ ...initialState });
			useServicesStore.getState().setAnalyticsUserProperties(loyaltyUserProperties(undefined));
		};

		const openBenefit = (selectedBenefit: ApiLoyaltyCard) => set({ selectedBenefit });

		const closeBenefit = () => set({ selectedBenefit: null, resortPickerCard: null });

		// A card that a member can redeem at several resorts asks which one first, in the same sheet.
		const openResortPicker = (resortPickerCard: ApiLoyaltyCard) => set({ resortPickerCard });

		const closeResortPicker = () => set({ resortPickerCard: null });

		const replayEntrance = () => set({ entranceKey: get().entranceKey + 1 });

		return {
			init,
			getApiLoyalty,
			reset,
			openBenefit,
			closeBenefit,
			openResortPicker,
			closeResortPicker,
			replayEntrance,
		};
	}),
);
